import Phaser from 'phaser';
import { ANIMATION } from '@/config/balance.config';

/**
 * Animator proceduralny dla peta. Bierze pojedynczy statyczny PNG i robi go
 * "żywym" przez kombinację:
 *
 *  - idle bob (delikatne unoszenie sin(t))
 *  - idle breathing (skala 1.0 ↔ 1.04 sin(t))
 *  - movement tilt (pochylenie w stronę ruchu, smooth-damp)
 *  - mirror flip (sprite "patrzy" w stronę ruchu)
 *  - attack squish (krótkie ściśnięcie pionowe + rozciągnięcie poziome)
 *  - **attack lunge** — mini-rzut w stronę celu przy każdym hicie (headbutt feel):
 *      wind-up → forward burst → return, plus mały obrót w stronę celu
 *  - sparkle pulse (co kilka sekund mały rozbłysk skali — "życie")
 *  - shadow (eliptyczny cień pod petem, lekko skaluje się z bob'em)
 *
 * Wszystkie efekty są aplikowane W KAŻDEJ KLATCE przez `update()`.
 * Squish/lunge to one-shot wywoływane przez `triggerAttackHit(dirX, dirY)`.
 *
 * Pet entity utrzymuje `logicalX/Y` (gdzie naprawdę "jest"), a animator zwraca
 * `{offsetX, offsetY}` które dodawane są do sprite.x/y. Dzięki temu logika ruchu
 * (FSM) jest oddzielona od warstwy wizualnej.
 */
export class PetAnimator {
  readonly shadow: Phaser.GameObjects.Ellipse;

  // Stałe per-instancja (różnicowanie żeby pety nie animowały się w synchro)
  private readonly phaseOffset: number = Math.random() * Math.PI * 2;
  private readonly baseScale: number;

  // Bieżące wartości (lerp'owane przez update)
  private smoothedVx = 0;
  private smoothedVy = 0;
  private currentTilt = 0;

  // Squish — multiplikatory skali, w spoczynku 1.0, podczas ataku 1.28 / 0.74
  private squishX = 1;
  private squishY = 1;

  // Sparkle pulse — co X sekund pet dostaje krótki bonus skali
  private nextSparkleAt: number;

  // Lunge — przy każdym hicie pet "rzuca się" w stronę celu i wraca
  private lungeStartedAt = 0;
  private lungeDirX = 0;
  private lungeDirY = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly sprite: Phaser.GameObjects.Image,
    baseScale: number,
    logicalX: number,
    logicalY: number,
  ) {
    this.baseScale = baseScale;
    this.nextSparkleAt = scene.time.now + this.randomSparkleDelay();

    const sh = ANIMATION.shadow;
    this.shadow = scene.add.ellipse(
      logicalX,
      logicalY + sh.offsetYpx,
      sh.width,
      sh.height,
      0x000000,
      sh.alpha,
    );
    this.shadow.setDepth(this.sprite.depth - 1);
  }

  /**
   * Aktualizuje wszystkie ciągłe efekty.
   * Zwraca `{offsetX, offsetY}` do dodania do sprite.x/y (bob + lunge).
   */
  update(time: number, dt: number, vx: number, vy: number, logicalX: number, logicalY: number): { offsetX: number; offsetY: number } {
    const cfg = ANIMATION.pet;

    // 1) Smooth velocity (lowpass) — dla płynnego tilt'u
    this.smoothedVx = this.smoothedVx * cfg.velocitySmoothing + vx * (1 - cfg.velocitySmoothing);
    this.smoothedVy = this.smoothedVy * cfg.velocitySmoothing + vy * (1 - cfg.velocitySmoothing);
    const speed = Math.hypot(this.smoothedVx, this.smoothedVy);
    const moving = speed > 20;

    // 2) Idle bob + breath
    const t = time / 1000 + this.phaseOffset;
    const bobMult = moving ? cfg.movingBobMult : 1;
    const bobY = Math.sin(t * Math.PI * 2 * cfg.bobFrequencyHz * bobMult) * cfg.bobAmplitudePx * bobMult;
    const breath = 1 + Math.sin(t * Math.PI * 2 * cfg.breathFrequencyHz) * cfg.breathAmplitude;

    // 3) Lunge — mini-rzut w stronę celu, aktywny po triggerAttackHit
    let lungeOffsetX = 0;
    let lungeOffsetY = 0;
    let lungeRotation = 0;
    const lungeElapsed = time - this.lungeStartedAt;
    if (lungeElapsed >= 0 && lungeElapsed < cfg.lungeDurationMs) {
      const lt = lungeElapsed / cfg.lungeDurationMs;            // 0..1
      // Krzywa: wind-up cofa się do tyłu, potem mocny skok do przodu, potem powolny return
      let curve: number;
      if (lt < cfg.lungeWindupRatio) {
        // wind-up: 0 → -0.3 (cofnięcie)
        curve = -(lt / cfg.lungeWindupRatio) * 0.3;
      } else if (lt < cfg.lungeWindupRatio + 0.18) {
        // forward burst: -0.3 → +1.0
        const burstT = (lt - cfg.lungeWindupRatio) / 0.18;
        curve = -0.3 + burstT * 1.3;
      } else {
        // return: +1.0 → 0
        const returnT = (lt - cfg.lungeWindupRatio - 0.18) / (1 - cfg.lungeWindupRatio - 0.18);
        curve = 1.0 * (1 - returnT);
      }
      lungeOffsetX = this.lungeDirX * curve * cfg.lungeDistancePx;
      lungeOffsetY = this.lungeDirY * curve * cfg.lungeDistancePx;
      // Obrót: tylko w peak'u lunge'a (curve > 0.5)
      lungeRotation = Math.max(0, curve) * cfg.lungeRotationRad * Math.sign(this.lungeDirX || 0.01);
    }

    // 4) Tilt — pochylenie w stronę ruchu (proporcjonalne do prędkości X) + lunge rotation
    const targetTilt = moving
      ? Phaser.Math.Clamp(this.smoothedVx / 300, -1, 1) * cfg.maxTiltRad
      : 0;
    this.currentTilt += (targetTilt - this.currentTilt) * cfg.tiltLerp;
    this.sprite.rotation = this.currentTilt + lungeRotation;

    // 5) Mirror flip — pet "patrzy" w stronę ruchu LUB w stronę lunge'a
    if (this.smoothedVx > 30) {
      this.sprite.flipX = false;
    } else if (this.smoothedVx < -30) {
      this.sprite.flipX = true;
    } else if (lungeElapsed >= 0 && lungeElapsed < cfg.lungeDurationMs) {
      // W trakcie ataku nie ma znaczącej prędkości — kierujemy się lunge dir
      if (this.lungeDirX > 0.3) this.sprite.flipX = false;
      else if (this.lungeDirX < -0.3) this.sprite.flipX = true;
    }

    // 6) Squish — lerp do 1.0 (po triggerze ataku odjeżdża i wraca)
    this.squishX += (1 - this.squishX) * cfg.attackReturnLerp;
    this.squishY += (1 - this.squishY) * cfg.attackReturnLerp;

    // 7) Sparkle pulse — krótki bonus skali co kilka sekund
    let sparklePulse = 1;
    if (time >= this.nextSparkleAt) {
      const elapsed = (time - this.nextSparkleAt);
      if (elapsed <= 200) {
        sparklePulse = 1 + Math.sin((elapsed / 200) * Math.PI) * 0.12;
      } else {
        this.nextSparkleAt = time + this.randomSparkleDelay();
      }
    }

    // 8) Finalne skale: base × breath × squish × sparkle
    this.sprite.scaleX = this.baseScale * breath * this.squishX * sparklePulse;
    this.sprite.scaleY = this.baseScale * breath * this.squishY * sparklePulse;

    // 9) Cień — pozycja zawsze przy LOGICZNYM Y (nie bob'uje, nie lunge'uje)
    const sh = ANIMATION.shadow;
    this.shadow.x = logicalX;
    this.shadow.y = logicalY + sh.offsetYpx;
    const shadowScale = 1 - Math.max(0, -bobY) / 12;
    this.shadow.scaleX = shadowScale;
    this.shadow.scaleY = shadowScale;

    void dt;
    return { offsetX: lungeOffsetX, offsetY: bobY + lungeOffsetY };
  }

  /**
   * Wywoływany w momencie zadania obrażeń destructiblowi.
   *  Pet się ściska (squish & stretch) + rzuca w stronę celu (lunge).
   *  Biały flash leci na CEL, nie na peta — to robi DestructibleAnimator.triggerHit.
   *
   * @param dirX  Znormalizowany X kierunku do celu (od peta do destructibla).
   * @param dirY  Znormalizowany Y kierunku do celu.
   */
  triggerAttackHit(dirX = 0, dirY = 0): void {
    const cfg = ANIMATION.pet;
    this.squishX = cfg.attackSquishX;
    this.squishY = cfg.attackSquishY;
    this.lungeDirX = dirX;
    this.lungeDirY = dirY;
    this.lungeStartedAt = this.scene.time.now;     // ta sama oś czasu co `time` w update()
  }

  destroy(): void {
    this.shadow.destroy();
  }

  private randomSparkleDelay(): number {
    const cfg = ANIMATION.pet;
    const min = cfg.blinkSparkleEverySecMin * 1000;
    const max = cfg.blinkSparkleEverySecMax * 1000;
    return min + Math.random() * (max - min);
  }
}
